import { apiService } from './api';
import { LoginCredentials, RegisterOrganizationData, AuthResponse, User } from '@/types/auth';

// Two-Factor Authentication pending response
export interface TwoFactorPendingResponse {
  requiresTwoFactor: true;
  partialToken: string;
  userId: string;
  methods: ('totp' | 'backup')[];
}

// Combined login response type
export type LoginResponse = AuthResponse | TwoFactorPendingResponse;

// Type guard for 2FA pending
export function isTwoFactorPending(response: LoginResponse): response is TwoFactorPendingResponse {
  return 'requiresTwoFactor' in response && response.requiresTwoFactor === true;
}

/**
 * Decode a JWT payload without signature verification.
 * Returns milliseconds until expiry, or 0 if already expired / unparseable.
 */
export function getTokenRemainingMs(token: string): number {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (!payload.exp) return 0;
    return Math.max(0, payload.exp * 1000 - Date.now());
  } catch {
    return 0;
  }
}

export class AuthService {
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    const response = await apiService.post<{ success: boolean; requiresTwoFactor?: boolean; data: any }>('/v1/auth/login', credentials);

    if (response.success && response.data) {
      // Check for 2FA pending response
      if (response.requiresTwoFactor || response.data.partialToken) {
        return {
          requiresTwoFactor: true,
          partialToken: response.data.partialToken,
          userId: response.data.userId,
          methods: response.data.methods || ['totp', 'backup'],
        };
      }

      const { user, organization, accessToken, refreshToken } = response.data;
      const tokens = { accessToken, refreshToken };

      apiService.setTokens(tokens.accessToken, tokens.refreshToken);
      localStorage.setItem('user', JSON.stringify(user));

      return { user, tokens, organization };
    }

    throw new Error('Login failed');
  }

  /**
   * Verify 2FA token and complete login
   */
  async verify2FA(partialToken: string, token: string): Promise<AuthResponse> {
    const response = await apiService.post<{ success: boolean; data: any }>(
      '/v1/auth/2fa/verify',
      { partialToken, token }
    );

    if (response.success || response.data?.accessToken) {
      const data = response.data || response;
      const { user, accessToken, refreshToken } = data;
      const tokens = { accessToken, refreshToken };

      apiService.setTokens(tokens.accessToken, tokens.refreshToken);
      localStorage.setItem('user', JSON.stringify(user));

      return { user, tokens };
    }

    throw new Error('2FA verification failed');
  }

  /**
   * Verify backup code and complete login
   */
  async verifyBackupCode(partialToken: string, code: string): Promise<AuthResponse> {
    const response = await apiService.post<{ success: boolean; data: any; warning?: string }>(
      '/v1/auth/2fa/verify-backup',
      { partialToken, code }
    );

    if (response.success || response.data?.accessToken) {
      const data = response.data || response;
      const { user, accessToken, refreshToken, warning } = data;
      const tokens = { accessToken, refreshToken };

      apiService.setTokens(tokens.accessToken, tokens.refreshToken);
      localStorage.setItem('user', JSON.stringify(user));

      return { user, tokens, warning };
    }

    throw new Error('Backup code verification failed');
  }

  async registerOrganization(data: RegisterOrganizationData): Promise<AuthResponse> {
    const response = await apiService.post<{ success: boolean; data: any }>('/v1/auth/register-organization', data);
    
    if (response.success && response.data) {
      const { user, organization, accessToken, refreshToken } = response.data;
      const tokens = { accessToken, refreshToken };
      
      apiService.setTokens(tokens.accessToken, tokens.refreshToken);
      localStorage.setItem('user', JSON.stringify(user));
      
      return { user, tokens, organization };
    }
    
    throw new Error('Organization registration failed');
  }

  async logout(): Promise<void> {
    try {
      await apiService.post('/v1/auth/logout');
    } catch (error) {
      // Continue with logout even if API call fails
    } finally {
      apiService.logout();
    }
  }

  async getCurrentUser(): Promise<User | null> {
    try {
      const response = await apiService.get<{ success: boolean; data: { user: User } }>('/v1/auth/me');
      if (response.success) {
        localStorage.setItem('user', JSON.stringify(response.data.user));
        return response.data.user;
      }
    } catch (error: any) {
      // Only logout on actual auth failures, not legal acceptance blocks (451)
      if (error?.response?.status !== 451) {
        apiService.logout();
      }
    }
    return null;
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    const response = await apiService.post<{ success: boolean; accessToken?: string; refreshToken?: string }>(
      '/v1/auth/change-password',
      { currentPassword, newPassword }
    );

    if (!response.success) {
      throw new Error('Password change failed');
    }
    // A password change signs out every session; the backend returns new tokens for this one.
    if (response.accessToken && response.refreshToken) {
      apiService.setTokens(response.accessToken, response.refreshToken);
    }
  }

  getStoredUser(): User | null {
    try {
      const userStr = localStorage.getItem('user');
      return userStr ? JSON.parse(userStr) : null;
    } catch {
      return null;
    }
  }

  isAuthenticated(): boolean {
    const token = localStorage.getItem('accessToken');
    const user = this.getStoredUser();
    if (!token || !user) return false;
    return getTokenRemainingMs(token) > 0;
  }

  hasPermission(permission: string): boolean {
    const user = this.getStoredUser();
    if (!user) return false;
    
    // Admin has all permissions
    if (user.permissions.includes('*')) return true;
    
    return user.permissions.includes(permission);
  }

  hasRole(role: string | string[]): boolean {
    const user = this.getStoredUser();
    if (!user) return false;
    
    const roles = Array.isArray(role) ? role : [role];
    return roles.includes(user.role);
  }

  async validateInvitation(token: string): Promise<{
    valid: boolean;
    email?: string;
    role?: string;
    organizationName?: string;
    expiresAt?: string;
    invitedBy?: string;
    error?: string;
  }> {
    const response = await apiService.get<{ success: boolean; data: any }>(`/v1/auth/invitations/validate?token=${encodeURIComponent(token)}`);

    if (response.success) {
      return response.data;
    }

    throw new Error('Failed to validate invitation');
  }

  async acceptInvitation(data: {
    token: string;
    password: string;
    firstName: string;
    lastName: string;
    legalAccepted?: boolean;
    profile?: {
      title?: string;
      department?: string;
      licenseNumber?: string;
      specialties?: string[];
      phoneNumber?: string;
    };
  }): Promise<AuthResponse> {
    const response = await apiService.post<{ success: boolean; data: any }>('/v1/auth/invitations/accept', data);

    if (response.success && response.data) {
      const { user, organization, accessToken, refreshToken } = response.data;
      const tokens = { accessToken, refreshToken };

      apiService.setTokens(tokens.accessToken, tokens.refreshToken);
      localStorage.setItem('user', JSON.stringify(user));

      return { user, tokens, organization };
    }

    throw new Error('Failed to accept invitation');
  }
}

export const authService = new AuthService();

// Utility function to get auth header for API requests
export const getAuthHeader = () => {
  const token = localStorage.getItem('accessToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};